// dependencies
import React, { useRef } from "react";
import { z } from "zod";

// components
import {
	FormCheckboxGroup,
	FormDatePicker,
	FormField,
	FormMultipleSelect,
	PanelFilters,
} from "@performyard/py-components";

import { MultipleSelectEmployee } from "../../../../../../components/Inputs/SelectEmployee";
import { SingleSelectGoalAlignment } from "../../../../../../components/Inputs/SelectGoalAlignment";

// hooks
import { useCompanySettings, useGroups } from "../../../../../../api/hooks";
import { useOutlooks } from "../../../../../../api/hooks/defs/outlook";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

// services
import { IStrategyCategory } from "../../../../../../api/services";

// utils
import { createFetchCategoryOptions, createFetchGroupOptions, createFetchOutlookOptions } from "./filterDataFetchers";

const statusOptions = [
	{ label: "Active", value: "active" },
	{ label: "Completed", value: "done" },
	{ label: "Archived", value: "archived" },
];

const transparencyOptions = [
	{ label: "Public", value: "public" },
	{ label: "Private", value: "private" },
];

const GoalsListFiltersSchema = z.object({
	assignee: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	groups: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	createdDateAfter: z.date().nullable().optional(),
	createdDateBefore: z.date().nullable().optional(),
	dueDateAfter: z.date().nullable().optional(),
	dueDateBefore: z.date().nullable().optional(),
	category: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	status: z.array(z.string()).default([]),
	transparency: z.array(z.string()).default([]),
	outlook: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })).default([]),
	showSharedOnly: z.array(z.string()).default([]),
	alignedTo: z
		.object({
			id: z.string(),
			name: z.string(),
			status: z.string(),
			color: z.string(),
			assignee: z.string(),
			created: z.string(),
			due: z.string(),
			target: z.number(),
			goalValue: z.number(),
			progress: z.number(),
			categories: z.array(z.string()),
		})
		.nullable()
		.optional(),
});

export type GoalsFilters = z.infer<typeof GoalsListFiltersSchema>;

interface GoalsFiltersProps {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<GoalsFilters>;
	onApplyFilters: (filters: Partial<GoalsFilters>) => void;
	isGroupFieldVisible?: boolean;
}

const GoalsListFilters = ({
	isOpen,
	onClose,
	filters,
	onApplyFilters,
	isGroupFieldVisible = true,
}: GoalsFiltersProps) => {
	const { data: companySettings } = useCompanySettings();
	const { value: ffGoalsDashboard } = useFeatureFlagQuery("ff-is-goal-overview-and-alignment-enabled", false);
	const goalDashboardFeatureEnabled =
		companySettings?.functions?.goalDashboard?.state === "enabled" && ffGoalsDashboard;

	const { value: groupsFeatureVisible } = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const groupsEnabled = companySettings?.functions?.groups?.state === "enabled" && groupsFeatureVisible;

	const { data: groupsData } = useGroups();
	const { data: outlooksData } = useOutlooks();

	const categoriesCache = useRef<IStrategyCategory[]>([]);

	const handleSubmit = ({
		value,
	}: {
		value: Partial<GoalsFilters>;
		resetForm: () => void;
		setError: (field: keyof GoalsFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };

		// Remove empty, null, or undefined fields
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (
				filterValue === null ||
				filterValue === undefined ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			) {
				delete formattedState[key];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	const defaultValues: GoalsFilters = {
		assignee: filters.assignee || [],
		groups: filters.groups || [],
		createdDateAfter: filters.createdDateAfter,
		createdDateBefore: filters.createdDateBefore,
		dueDateAfter: filters.dueDateAfter,
		dueDateBefore: filters.dueDateBefore,
		category: filters.category || [],
		status: filters.status || [],
		transparency: filters.transparency || [],
		outlook: filters.outlook || [],
		showSharedOnly: filters.showSharedOnly || [],
		alignedTo: filters.alignedTo ?? undefined,
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={GoalsListFiltersSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string }[]>
					id="assignee"
					label="Assignee"
					input={(field) => (
						<MultipleSelectEmployee
							value={field.value || []}
							onChange={field.onChange}
							placeholder="Make a selection"
						/>
					)}
				/>

				{groupsEnabled && isGroupFieldVisible && (
					<FormMultipleSelect<{ id: string; name: string }>
						id="groups"
						label="Group"
						fetchOptions={createFetchGroupOptions(groupsData)}
						selectionVariant="combobox"
						placeholder="Select groups..."
					/>
				)}

				<FormDatePicker id="createdDateAfter" label="Created After Date" clearable />

				<FormDatePicker id="createdDateBefore" label="Created Before Date" clearable />

				<FormDatePicker id="dueDateAfter" label="Due Date After" clearable />

				<FormDatePicker id="dueDateBefore" label="Due Date Before" clearable />

				<FormMultipleSelect<{ id: string; name: string }>
					id="category"
					label="Category"
					fetchOptions={createFetchCategoryOptions(categoriesCache)}
					selectionVariant="combobox"
					placeholder="Make a selection"
				/>

				<FormCheckboxGroup id="status" label="Status" options={statusOptions} />

				<FormCheckboxGroup id="transparency" label="Transparency Level" options={transparencyOptions} />

				<FormMultipleSelect<{ id: string; name: string; color: string }>
					id="outlook"
					label="Outlook"
					fetchOptions={createFetchOutlookOptions(outlooksData)}
					selectionVariant="combobox"
					placeholder="Select a goal outlook"
					renderOption={(option) => (
						<div className="flex items-center gap-2">
							<div className="h-3 w-3 rounded-full" style={{ backgroundColor: option.value.color }} />
							<span>{option.label}</span>
						</div>
					)}
				/>

				<FormCheckboxGroup
					id="showSharedOnly"
					label="Show Shared Only"
					options={[{ label: "Show Shared Only", value: "True" }]}
				/>

				{goalDashboardFeatureEnabled && (
					<FormField<{
						id: string;
						name: string;
						status: string;
						color: string;
						assignee: string;
						created: string;
						due: string;
						target: number;
						goalValue: number;
						progress: number;
						categories: string[];
					} | null>
						id="alignedTo"
						label="Goal Alignment"
						input={(field) => (
							<SingleSelectGoalAlignment
								value={field.value}
								onChange={field.onChange}
								placeholder="Choose a goal"
							/>
						)}
					/>
				)}
			</div>
		</PanelFilters>
	);
};

export default GoalsListFilters;
